import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { isZitadelMode } from '@/app/urlParams';
import { AuthGateScreen } from '@/components/shared/AuthGateScreen';
import { buildGameThemeStyle } from '@/utils/gameThemeStyle';
import { appConfig } from '@app/config/appConfig';
import { useAuthGate } from '@hooks/useAuthGate';

import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';
import { ToastProvider } from './ToastProvider';

interface AppBootstrapGateProps {
  children: ReactNode;
}

/**
 * Inner component that runs the dev-session auth gate.
 * Only rendered in non-Zitadel mode.
 */
function DevSessionAuthGate({ children }: AppBootstrapGateProps) {
  const { isReady, error, phase } = useAuthGate();
  const gameThemeStyle = useMemo(() => buildGameThemeStyle(appConfig.socket.stageNumber), []);

  if (!isReady || error) {
    return <AuthGateScreen gameThemeStyle={gameThemeStyle} error={error} phase={phase} />;
  }

  return <>{children}</>;
}

/**
 * Outer bootstrap gate — always provides QueryProvider / ThemeProvider / ToastProvider.
 * In Zitadel mode the dev-session auth check is skipped entirely.
 */
export function AppBootstrapGate({ children }: AppBootstrapGateProps) {
  return (
    <QueryProvider>
      <ThemeProvider>
        <ToastProvider>
          {isZitadelMode ? (
            // Zitadel mode: token already injected in main.tsx — skip auth gate
            children
          ) : (
            <DevSessionAuthGate>{children}</DevSessionAuthGate>
          )}
        </ToastProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}
